import {
  ActivitySquare,
  BarChart2,
  Code,
  Cpu,
  Globe,
  Share2,
} from "lucide-react";
import { ReactElement } from "react";

export type TExperience = {
  company: string;
  icon: string;
  link: string;
  title: string;
  range: string;
  description: string;
  contributions: Array<string | ReactElement>;
  badges: Array<{
    text: string;
    icon: ReactElement;
  }>;
};

const iconStyle: Object = { width: 14, height: 14 };
const experiences: TExperience[] = [
  {
    company: "Dataroid",
    icon: "https://www.dataroid.com/favicon.ico",
    link: "https://www.dataroid.com/",
    title: "Software Engineer",
    range: "May 2025 - Present",
    description:
      "At Dataroid, an AI-powered digital analytics and customer engagement platform, I work within the AI team as part of every stage of AI-driven product development, from architecture decisions to implementation and delivery. The team operates with an AI-first engineering culture, actively integrating AI tools (Claude Code, Cursor, MCP) into the workflow to continuously improve code review, testing, and delivery processes.",
    contributions: [
      <>Developed the frontend for the <a href="https://www.dataroid.com/ai-agent-d/" target="_blank" rel="noopener noreferrer" style={{ color: "#536493" }}>AI Agent D</a> feature (an AI chatbot product). Contributed to technical decision-making on the backend side as well, being deeply involved in every aspect of the AI chatbot development.</>,
      <>Contributed to Dataroid's full panel redesign across both implementation and key frontend decisions throughout the process. <a href="https://www.linkedin.com/pulse/dataroids-panel-redesign-quieter-interface-where-data-merve-ata%C3%A7-an2yf/" target="_blank" rel="noopener noreferrer" style={{ color: "#536493" }}>Read more</a>.</>,
      "Developed the frontend for three separate AI-powered data analysis features. Solely determined the frontend architecture for all three. Supported backend architecture design for one, and fully designed the backend architecture for another.",
    ],
    badges: [
      {
        text: "AI Chatbot",
        icon: <Cpu style={iconStyle} />,
      },
      {
        text: "Frontend Architecture",
        icon: <Code style={iconStyle} />,
      },
      {
        text: "Panel Redesign",
        icon: <ActivitySquare style={iconStyle} />,
      },
    ],
  },
  {
    company: "Papel",
    icon: "https://www.deliveryhero.com/wp-content/uploads/2019/08/DH-LOGO-02.png",
    link: "https://papel.com.tr/",
    title: "Software Developer",
    range: "January 2025 - April 2025 (4 months)",
    description:
      "At Papel, a financial solutions company, I contributed to the development of micro frontend applications, while driving technical improvements and architectural decisions focused on scalability and performance.",
    contributions: [
      "Found and implemented suitable solutions to remove '!important' CSS styles in the consumer wallet application.",
      "Transferred SCSS styles in a completed web application to mixins and style variables based on application requirements.",
      "Re-establishment and development of the Register structure of a Fintech application.",
    ],
    badges: [
      {
        text: "Microfrontend",
        icon: <Globe style={iconStyle} />,
      },
    ],
  },
  {
    company: "Delivery Hero",
    icon: "https://www.deliveryhero.com/wp-content/uploads/2019/08/DH-LOGO-02.png",
    link: "https://www.deliveryhero.com/",
    title: "Software Engineer II",
    range: "July 2023 - July 2024 (1 year)",
    description:
      "Worked for a configurable pricing discount system. As the sole front-end specialist in the team, I was responsible for the overwhelming majority of the decisions, development and maintenance, technical improvements, communication with the other teams, integrations, and creating documentation on front-end applications.",
    contributions: [
      "Wrote an API document RFC containing all endpoints about API and designed a front-end structure accordingly.",
      "To improve user experience on a front-end application, I identified six bottlenecks and solutions (code-splitting, lazy loading, tree shaking, Webpack optimization, etc.) to improve loading speed. I developed most solutions and delegated some to other team members by creating tasks for each. The Webpack bundle size was reduced by 70%, resulting in a faster loading time.",
      "For a more stable user experience, I analyzed all endpoints, prepared a report on endpoint severity, timeout, retry count, GA (Google Analytics) events, and error management selection, collaborated with the product manager to create an error handling strategy and presented it. I prioritized services by calculating API callback durations & frequencies. The document was shared in a monthly engineering session to inspire other teams.",
      "Migrating a plugin to a new front-end application(Restaurant Panel), I handled the entire process, including designing the structure, creating implementation documents, coordination between teams, development, QA tests, CI/CD workflows, and integration of the new internal design library. I collaborated with front-end, back-end, and DevOps teams. My integration document was used as a template for other teams’ migrations.",
      "To create configurable billing contracts for the Joker feature on a vendor basis, I designed and analyzed the structure of the API and UI tasks. I’ve implemented its front-end.",
    ],
    badges: [
      {
        text: "Gloabl Impact: in 51 Countries",
        icon: <Globe style={iconStyle} />,
      },
      {
        text: "33k RPM Peak",
        icon: <Cpu style={iconStyle} />,
      },
      {
        text: "18.5k RPM Avg",
        icon: <ActivitySquare style={iconStyle} />,
      },
      {
        icon: <Code style={iconStyle} />,
        text: "3 FE Application, 2 B2B & 3 BFF APIs, 36+ Services",
      },
    ],
  },
  {
    company: "Delivery Hero",
    icon: "https://www.deliveryhero.com/wp-content/uploads/2019/08/DH-LOGO-02.png",

    link: "https://www.deliveryhero.com/",
    title: "Software Engineer I",
    range: "April 2022 - July 2023 (1 year 3 months)",
    description: "",
    contributions: [
      "Improved A/B testing which helped product achieve a total of 87% revenue increase over the course of its lifetime.",
      "A front-end plugin was redesigned and I took the lead in rewriting/designing the new architecture for this project. After the project was completed, I presented the project to all Delivery Hero developers. The restaurant conversion rate increased by 1.5x after the launch.",
      "To support multiple languages for 30+ countries, I integrated Weblate to manage language file changes through its panel to eliminate developer effort, which previously took half a day each week on average.",
      "To improve response time and stability, I contributed to the database partition implementation for a table that is one of the most critical and extensive tables. (approx. 1.6B rows)",
    ],
    badges: [
      {
        text: "Gloabl Impact: in 51 Countries",
        icon: <Globe style={iconStyle} />,
      },
      {
        text: "33k RPM Peak",
        icon: <Cpu style={iconStyle} />,
      },
      {
        text: "18.5k RPM Avg",
        icon: <ActivitySquare style={iconStyle} />,
      },
      {
        icon: <Code style={iconStyle} />,
        text: "3 FE Application, 2 B2B & 3 BFF APIs, 36+ Services",
      },
    ],
  },
  {
    company: "Yemeksepeti",
    icon: "https://www.deliveryhero.com/wp-content/uploads/2019/08/DH-LOGO-02.png",

    link: "https://www.yemeksepeti.com/",
    title: "Software Engineer I",
    range: "April 2021 - April 2022 (1 year)",
    description: "",
    contributions: [
      "Contributed to the development of CI/CD pipeline required via GitHub Actions.",
      "Designed and developed of GCP Airflow DAGs structure for Delivery Hero Adtech reporting project.",
      "I completed the development and CI/CD steps while collaborating with internal teams to integrate the Joker sub-plugin into Delivery Hero’s back office plugin. It was the first integration of a sub-plugin, and it became an exemplary development for internal teams.",
    ],
    badges: [
      {
        text: "Gloabl Impact: in 51 Countries",
        icon: <Globe style={iconStyle} />,
      },
      {
        text: "33k RPM Peak",
        icon: <Cpu style={iconStyle} />,
      },
      {
        text: "18.5k RPM Avg",
        icon: <ActivitySquare style={iconStyle} />,
      },
      {
        icon: <Code style={iconStyle} />,
        text: "3 FE Application, 2 B2B & 3 BFF APIs, 36+ Services",
      },
    ],
  },
  {
    company: "Sehatek",
    icon: "https://www.deliveryhero.com/wp-content/uploads/2019/08/DH-LOGO-02.png",

    link: "https://sehatek.com.tr/",
    title: "Web Developer",
    range: "October 2018 - February 2021 (2 years 4 months)",
    description: "",
    contributions: [
      "Company Website & UzmanAkustik Website: Developed and maintained the websites, optimized SEO and managed Google Ads that the company uses to promote to customers.",
      "Customer Collection Portal: Developed the portal utilized by sales personnel and their managers that included features such as collection reports, target analysis, and customer portfolio management.",
      "Social Media Post Planner: Contributed to developing a web application for scheduling social media posts across platforms like Facebook, Twitter, and Pinterest, which was open-sourced (animation) later.",
    ],
    badges: [
      {
        text: "SEO & Google Ads",
        icon: <BarChart2 style={iconStyle} />,
      },
      {
        text: "Open Source Contributor",
        icon: <Share2 style={iconStyle} />,
      },
    ],
  },
];

export default experiences;
